import React, { useRef, useState } from "react";
import axios from "axios";
import { PulseLoader } from "react-spinners";
import StepShell from "./StepShell";
import copy from "../copy";

// Spanish postal codes are 5 digits, province prefix 01-52 (Álava 01 ... Melilla 52).
const hasValidProvince = (code) => {
  const province = Number(code.slice(0, 2));
  return province >= 1 && province <= 52;
};

const PostalStep = ({ data, handleChange, onNext, onBack }) => {
  const [code, setCode] = useState(data.postal_code || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const latest = useRef("");

  // Resolves a full 5-digit code. Resolves to true when the code is accepted.
  // The lookup service only adds the town name; if it doesn't know the code or
  // is down we still accept a code with a valid province, so a real customer
  // is never blocked by a gap in someone else's database.
  const resolve = async (value) => {
    latest.current = value;
    setLoading(true);
    setError("");
    handleChange("place_name", "");
    handleChange("postal_code", "");

    if (!hasValidProvince(value)) {
      setLoading(false);
      setError(copy.postal.invalid);
      return false;
    }

    let place = "";
    try {
      const res = await axios.get(`https://api.zippopotam.us/es/${value}`);
      place = res?.data?.places?.[0]?.["place name"] || "";
    } catch (err) {
      // Unknown code or service unreachable: continue without a town name.
    }

    if (latest.current !== value) return false; // user kept typing
    if (place) handleChange("place_name", place);
    handleChange("postal_code", value);
    setLoading(false);
    return true;
  };

  const onChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 5);
    setCode(value);
    if (value.length === 5) {
      resolve(value);
    } else {
      latest.current = "";
      setLoading(false);
      setError("");
      handleChange("place_name", "");
      handleChange("postal_code", "");
    }
  };

  // 4 digits = the leading zero was left off (e.g. 8001 for Barcelona's 08001).
  // It's added on submit rather than while typing, so someone still typing
  // "2800" isn't turned into "02800" mid-way.
  const canSubmit =
    (code.length === 4 || code.length === 5) && !loading && !error;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const full = code.length === 4 ? `0${code}` : code;
    if (full !== code) setCode(full);
    if (data.postal_code === full) return onNext();
    if (await resolve(full)) onNext();
  };

  return (
    <StepShell title={copy.postal.title} onBack={onBack}>
      <form className="rp-form" noValidate onSubmit={submit}>
        <input
          className="rp-input"
          type="text"
          name="postal_code"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          aria-label={copy.postal.title}
          aria-invalid={Boolean(error)}
          value={code}
          onChange={onChange}
        />
        <div className="rp-status" aria-live="polite">
          {loading ? (
            <PulseLoader color="#001c34" size={8} />
          ) : error ? (
            <span className="rp-error">{error}</span>
          ) : data.place_name ? (
            <span className="rp-ok">{data.place_name}</span>
          ) : null}
        </div>
        <div className="rp-actions">
          <button type="submit" className="rp-btn" disabled={!canSubmit}>
            {copy.next}
          </button>
        </div>
      </form>
    </StepShell>
  );
};

export default PostalStep;
